// core/stores/sessionStore.ts — Zustand store for the session/auth state.
//
// Mirrors the legacy localStorage['truxsocial:accounts'] shape exactly:
//   - an array of Account records, each with a useThisAsActive flag
//   - no separate "current session" key — the active account is derived
//     by finding the one with useThisAsActive: true
//
// Persistence: the `accounts` array is persisted to localStorage under
// 'truxsocial:accounts' so the data survives page reloads.
//
// React components must NOT import this store directly; use hooks/useSession.ts.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Account } from '@/core/models';

export const ACCOUNTS_KEY = 'truxsocial:accounts';

interface SessionState {
  accounts: Account[];

  // --- setters used by authService ---
  setAccounts: (accounts: Account[]) => void;
  addAccount: (account: Account) => void;

  // --- test helper ---
  // Resets in-memory state (accounts to []) and removes the persisted key.
  // Only called from test beforeEach; not exposed via hooks.
  reset: () => void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      accounts: [],

      setAccounts: (accounts) => set({ accounts }),

      addAccount: (account) =>
        set((s) => ({ accounts: [...s.accounts, account] })),

      reset: () => {
        localStorage.removeItem(ACCOUNTS_KEY);
        set({ accounts: [] });
      },
    }),
    {
      name: ACCOUNTS_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist the accounts array; actions are not serialisable.
      partialize: (s) => ({ accounts: s.accounts }),
    },
  ),
);
